import type { ExpertsData, FulfillmentData, GeneralData, PipelineRow, SalesData } from "./useDashboardData";
import { BRAND_PROJECT_INTERNAL_STATUSES } from "../videoProduction/helper";

export interface MetricItem {
    label: string;
    val: number;
    change?: number;
    pct?: number;
    target?: number;
    color?: string;
    alert?: string;
    up?: boolean;
    display?: string;
    pctLabel?: string;
    sub?: string;
    prefix?: string;
    suffix?: string;
    linkUrl?: string;
}

export interface Segment {
    label: string;
    val: number;
    color: string;
}

export type TabId = "overview" | "general" | "account" | "experts" | "fulfillment" | "sales" | "roadmap";

export const PERIODS = ["Today", "7d", "30d", "Quarter", "Year", "5Y", "10Y", "Custom"];

export const DONUT_PALETTE = ["#E8913A", "#2C3E50", "#1C7A5A", "#D4546A", "#2D9CDB", "#9B59B6", "#7F8C8D"];

export const PIPELINE_COLORS: Record<string, string> = {
    new_pending: "#E8913A",
    waiting_on_brief: "#D4546A",
    internal_approval: "#2D9CDB",
    expert_proposal: "#9B59B6",
    client_selection: "#1C7A5A",
    scheduling: "#4A90B8",
    scheduled: "#5A9E6F",
    filming: "#E8913A",
    editing: "#1C7A5A",
    video_review: "#D4546A",
    completed: "#2C3E50",
};

export const DASH_TABS: { id: TabId; label: string; color: string }[] = [
    // Overview is not a category: its "color" is the neutral text token, so it
    // must flip with the theme (a literal near-black is invisible in dark).
    { id: "overview", label: "Overview", color: "var(--text)" },
    { id: "general", label: "General", color: "#C8A97E" },
    { id: "account", label: "Account Management", color: "#4A90B8" },
    { id: "experts", label: "Expert Team", color: "#5A9E6F" },
    { id: "fulfillment", label: "Fulfillment", color: "#D85A30" },
    { id: "sales", label: "Sales", color: "#6C5CE7" },
    { id: "roadmap", label: "Roadmap", color: "#9B59B6" },
];

export const AM_STATUS_URL =
    "/video-production/projects?status=Expert Proposals,Experts Proposal Review,Experts Proposal Review Denied,Brief Review,Experts Selection,Scheduling";

export const PIPELINE_STATUS_FILTER: Record<string, string> = {
    new_pending: "Pending Approval",
    waiting_on_brief: "Waiting For Brief",
    internal_approval: "Expert Proposals",
    expert_proposal: "Experts Proposal Review",
    client_selection: "Experts Selection",
    scheduling: "Scheduling",
    scheduled: "Pre-Production",
    filming: "Filming",
    editing: "Editing",
    video_review: "Video Review",
    completed: "complete",
};

export const projectsStatusUrl = (status: string) =>
    `/video-production/projects?status=${encodeURIComponent(status)}`;

export const toGeneralCards = (g: GeneralData | null, periodQuery?: string): MetricItem[] =>
    g
        ? [
              { label: "Total Brands", val: g.brand_accounts.val, change: g.brand_accounts.change, pct: g.brand_accounts.pct, target: g.brand_accounts.target ?? undefined, color: "#E8913A", linkUrl: "/brands" },
              { label: "New Brands", val: g.new_brands.val, change: g.new_brands.change, pct: g.new_brands.pct, target: g.new_brands.target ?? undefined, color: "#2D9CDB", linkUrl: periodQuery ? `/brands?${periodQuery}` : "/brands" },
              { label: "Total Products", val: g.total_products.val, change: g.total_products.change, pct: g.total_products.pct, target: g.total_products.target ?? undefined, color: "#1C7A5A", linkUrl: "/products" },
          ]
        : [];

export const toOpsCards = (g: GeneralData | null, periodQuery?: string): MetricItem[] =>
    g
        ? [
              { label: "Total Orders", val: g.total_orders.val, change: g.total_orders.change, pct: g.total_orders.pct, target: g.total_orders.target ?? undefined, color: "#2C3E50", linkUrl: "/video-production/orders" },
              { label: "New Orders", val: g.new_orders.val, change: g.new_orders.change, pct: g.new_orders.pct, target: g.new_orders.target ?? undefined, color: "#9B59B6", alert: g.new_orders.unassigned > 0 ? `${g.new_orders.unassigned} Unassigned` : undefined, linkUrl: periodQuery ? `/video-production/orders?${periodQuery}` : "/video-production/orders" },
              { label: "Active Projects", val: g.active_projects.val, change: g.active_projects.change, pct: g.active_projects.pct, target: g.active_projects.target ?? undefined, color: "#2D9CDB", linkUrl: "/video-production/projects?status=active" },
              { label: "Final Delivered", val: g.completed.val, change: g.completed.change, pct: g.completed.pct, target: g.completed.target ?? undefined, color: "#1C7A5A", linkUrl: "/video-production/projects?status=complete" },
          ]
        : [];

export const toPipeline = (rows: PipelineRow[]) =>
    rows.map((r) => {
        const status = PIPELINE_STATUS_FILTER[r.key];
        return {
            ...r,
            color: PIPELINE_COLORS[r.key] || "#7F8C8D",
            tc: "#FFF",
            linkUrl: status ? projectsStatusUrl(status) : undefined,
        };
    });

export const toExpertsTop = (e: ExpertsData | null, periodQuery?: string): MetricItem[] =>
    e
        ? [
              { label: "Total Experts", val: e.total_experts.val, change: e.total_experts.change, pct: e.total_experts.pct, target: e.total_experts.target ?? undefined, color: "#2C3E50", up: (e.total_experts.change ?? 0) >= 0, linkUrl: "/experts" },
              { label: "New This Period", val: e.new_this_period.val, change: e.new_this_period.change, pct: e.new_this_period.pct, target: e.new_this_period.target ?? undefined, color: "#9B59B6", up: (e.new_this_period.change ?? 0) >= 0, linkUrl: periodQuery ? `/experts?${periodQuery}` : "/experts" },
              { label: "Awaiting Proposals", val: e.awaiting_proposals.val, change: e.awaiting_proposals.change, pct: e.awaiting_proposals.pct, target: e.awaiting_proposals.target ?? undefined, color: "#D4546A", up: (e.awaiting_proposals.change ?? 0) >= 0, alert: e.awaiting_proposals.urgent > 0 ? `${e.awaiting_proposals.urgent} Urgent` : undefined, linkUrl: "/video-production/projects?status=Expert Proposals" },
              { label: "Total Experience", val: e.total_experience_years.val, sub: "Years", color: "#C8A97E" },
          ]
        : [];

export const toSalesCards = (s: SalesData | null, periodQuery?: string): MetricItem[] =>
    s
        ? [
              { label: "Leads", val: s.leads.val, change: s.leads.change, pct: s.leads.pct, color: "#6C5CE7", up: (s.leads.change ?? 0) >= 0, linkUrl: periodQuery ? `/sales/leads?${periodQuery}` : "/sales/leads" },
              { label: "PDP Audits", val: s.pdp_audits.val, change: s.pdp_audits.change, pct: s.pdp_audits.pct, color: "#2D9CDB", up: (s.pdp_audits.change ?? 0) >= 0, linkUrl: periodQuery ? `/sales/pdp-audit-reports?${periodQuery}` : "/sales/pdp-audit-reports" },
              { label: "Proposals", val: s.proposals?.val ?? 0, change: s.proposals?.change, pct: s.proposals?.pct, color: "#1C7A5A", up: (s.proposals?.change ?? 0) >= 0, linkUrl: periodQuery ? `/sales/proposals?${periodQuery}` : "/sales/proposals" },
          ]
        : [];

export const toSegments = (rows: any[], labelKeys: string[]): Segment[] =>
    (rows ?? [])
        .filter((r) => labelKeys.some((k) => r[k]))
        .map((r, i) => ({
            label: labelKeys.map((k) => r[k]).find(Boolean) || `Item ${i + 1}`,
            val: Number(r.total ?? r.count ?? 0),
            color:
                i < DONUT_PALETTE.length
                    ? DONUT_PALETTE[i]
                    : `hsl(${Math.round((i * 137.508) % 360)}, 62%, 55%)`,
        }));

// Deliverables are the videos on the Videos page, which shows project and
// non-project videos as separate views (the link opens the project view).
const projectSplit = (c: { project?: number; non_project?: number }) =>
    c.project == null || c.non_project == null
        ? undefined
        : `${c.project.toLocaleString()} project · ${c.non_project.toLocaleString()} non-project`;

export const toFulfillTop = (f: FulfillmentData | null, amStatusUrl: string): MetricItem[] =>
    f
        ? [
              { label: "Total Deliverables", val: f.total_deliverables.val, change: f.total_deliverables.change, target: f.total_deliverables.target ?? undefined, color: "#2C3E50", sub: projectSplit(f.total_deliverables), linkUrl: "/videos" },
              { label: "Waiting Estimated Time On Set", val: f.waiting_estimated_time.val, target: f.waiting_estimated_time.target, color: "#2D9CDB", linkUrl: `${amStatusUrl}&est_time_set=null` },
              { label: "Projects Needs Scheduling", val: f.projects_needs_scheduling?.val ?? 0, target: f.projects_needs_scheduling?.target ?? 30, color: "#E8913A", linkUrl: "/video-production/projects?status=Scheduling" },
              { label: "Projects In Pre-Production", val: f.projects_scheduled.val, target: f.projects_scheduled.target, color: "#9B59B6", alert: f.projects_scheduled.alert ?? undefined, linkUrl: "/video-production/projects?status=Pre-Production&film_within=30" },
          ]
        : [];

export const toFulfillMid = (f: FulfillmentData | null): MetricItem[] =>
    f
        ? [
              { label: "Projects Filming", val: f.projects_filming.val, target: f.projects_filming.target, color: "#E8913A", alert: f.projects_filming.alert ?? undefined, linkUrl: "/video-production/projects?status=Filming&exactStatus=true" },
              { label: "Projects Editing", val: f.projects_editing.val, target: f.projects_editing.target, color: "#1C7A5A", linkUrl: "/video-production/projects?status=Editing&exactStatus=true" },
              { label: "Projects In Video Review", val: f.projects_in_review.val, target: f.projects_in_review.target, color: "#D4546A", linkUrl: "/video-production/projects?status=Video Review" },
          ]
        : [];

export const toFulfillBot = (f: FulfillmentData | null): MetricItem[] =>
    f
        ? [
              { label: "Waiting On Approval", val: f.waiting_on_approval.val, target: f.waiting_on_approval.target, color: "#D4546A", sub: projectSplit(f.waiting_on_approval), alert: f.waiting_on_approval.overdue > 0 ? `${f.waiting_on_approval.overdue} Overdue` : undefined, linkUrl: "/videos?approved=false" },
              { label: "Approved Deliverables", val: f.approved_deliverables.val, change: f.approved_deliverables.change, target: f.approved_deliverables.target ?? undefined, color: "#1C7A5A", sub: projectSplit(f.approved_deliverables), linkUrl: "/videos?approved=true" },
          ]
        : [];

const S = BRAND_PROJECT_INTERNAL_STATUSES;

const INTERNAL_STATUS_STAGES: Record<string, { group: string; color: string; terminal?: boolean }> = {
    [S.READY_FOR_CLIENT]: { group: "Client Review", color: "#2D9CDB" },
    [S.SENT_TO_CLIENT]: { group: "Client Review", color: "#2D9CDB" },
    [S.FEEDBACK_READY_FOR_PRODUCER]: { group: "Feedback", color: "#E8913A" },
    [S.FEEDBACK_READY_FOR_POST]: { group: "Feedback", color: "#E8913A" },
    [S.POST_CHECK]: { group: "Post", color: "#9B59B6" },
    [S.POST_APPROVED]: { group: "Post", color: "#9B59B6" },
    [S.CLIENT_DELIVERED]: { group: "Delivered", color: "#0E9594", terminal: true },
    [S.ARCHIVED]: { group: "Delivered", color: "#6B7A8F", terminal: true },
};

const DEFAULT_STAGE = { group: "Other", color: "#0E9594" };

export const toInternalStatusRows = (f: FulfillmentData | null) =>
    (f?.internal_statuses ?? []).map((b) => ({
        label: b.label,
        val: b.val,
        ...(INTERNAL_STATUS_STAGES[b.label] ?? DEFAULT_STAGE),
        linkUrl: `/videos?internalStatus=${encodeURIComponent(b.label)}`,
    }));

export const sumSegments = (segs: Segment[]) => segs.reduce((s, x) => s + x.val, 0);
